/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-wrapper-object-types */
import "reflect-metadata";

import { StringHelper } from "@tylertech/shared-module";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { LogLevel } from "../logLevel";
import { getLoggerFor, ILogger } from "../logger";
import { MethodMetadata, METHOD_METADATA_KEY } from "./methodMetadata";
import { LoggerParamMetadata, PROPERTY_KEY_LOG_VALUES } from "./paramMetadata";
import { CLASS_METADATA_KEY, ClassMetadata } from "./classMetadata";

interface ICustomLogger
{
    log?: ILogger;
}


export type generalFunction = <This, Args extends Array<unknown>, TReturn>(this: This, ...params: Args) => TReturn;

interface Param
{
    readonly name?: string,
    readonly value: unknown;
}

export function isPromiseLike<T = unknown>(value: unknown): value is Promise<T>
{
    if (!value)
    {
        return false;
    }
    return value instanceof Promise
        || (typeof value === "object"
            && typeof value["then"] === "function");
}

export function getLogger(instance: ICustomLogger, targetName: string, classMetadata: ClassMetadata | undefined): ILogger
{
    if (classMetadata?.logPropertyName)
    {
        return instance[classMetadata.logPropertyName];
    }

    if (!instance.log)
    {
        instance.log = getLoggerFor(targetName);
    }

    return instance.log;
}

export function logAtLevel(log: ILogger, level: LogLevel, message: string, ...args: Array<unknown>): void
{
    switch (level)
    {
        case LogLevel.ERROR:
            log.error(message, ...args);
            break;
        case LogLevel.WARNING:
            log.warning(message, ...args);
            break;
        case LogLevel.DEBUG:
            log.debug(message, ...args);
            break;
        case LogLevel.TRACE:
            log.trace(message, ...args);
            break;
        case LogLevel.INFORMATION:
            log.information(message, ...args);
            break;
        default:
            log.warning(`Unknown LogLevel|${level}`);
            log.information(message, ...args);
            break;
    }
}

export function processArgs(logParamSettings: ReadonlyArray<LoggerParamMetadata>, params: unknown[]): Array<Param>
{
    const argsToLog: Array<Param> = [];
    for (let i = 0; i < params.length; i++)
    {
        const paramSetting: LoggerParamMetadata = logParamSettings[i] || {};
        const paramValue = paramSetting.project
            ? paramSetting.project(params[i])
            : params[i];
        argsToLog.push(
            paramSetting?.name
                ? {
                    name: paramSetting.name.toString(),
                    value: paramValue
                }
                : {
                    value: paramValue
                });
    }
    return argsToLog.length > 0 ? argsToLog : undefined;
}

export function stringifyParams(logParamSettings: ReadonlyArray<LoggerParamMetadata>, params: unknown[]): string
{
    const argsToLog = [];
    for (let i = 0; i < params.length; i++)
    {
        const paramSetting: LoggerParamMetadata = logParamSettings[i] || {};
        argsToLog[i] = stringifyParam(params[i], paramSetting);
    }
    return argsToLog.join(", ");
}

function stringifyParam(value: unknown, paramSetting?: LoggerParamMetadata): string | symbol | undefined
{
    if (paramSetting?.name)
    {
        return paramSetting.name;
    }

    if (paramSetting?.replacer)
    {
        return paramSetting.replacer(value);
    }

    return stringify(value);
}

function projectResult<T = unknown>(value: T, methodMetadata: MethodMetadata<T> | undefined): T
{
    return methodMetadata?.project
        ? methodMetadata.project(value)
        : value;
}

function stringifyResult<T = unknown>(value: T, methodMetadata: MethodMetadata<T> | undefined): string
{
    return methodMetadata?.replacer
        ? methodMetadata.replacer(value)
        : stringify(value);
}

export function stringify(value: unknown): string
{
    if (value === undefined)
    {
        return "undefined";
    }

    if (value === null)
    {
        return "null";
    }

    if (typeof value === "function")
    {
        return "[object Function]";
    }

    if (value instanceof Date)
    {
        return `[Date '${StringHelper.stringify(value.toLocaleString())}']`;
    }

    if (Array.isArray(value))
    {
        return `[Array ${value.length}]`;
    }

    if (typeof value === "string")
    {
        return `"${sanitizeForLog(value)}"`;
    }

    if (value instanceof Error)
    {
        return sanitizeForLog(`${value.name}: ${value.message}`);
    }

    try
    {
        // toString() can throw, so need a try/catch to not interrupt the original error handling
        return sanitizeForLog(value.toString());
    }
    catch
    {
        return "[unstringifiable]";
    }
}

function sanitizeForLog(s: string): string
{
    return s
        .replace(/[\r\n]+/g, " ")   // no forged log lines
        .replace(/%/g, "%%");       // no console printf substitution
}

interface LogInvocationOptions
{
    readonly level: LogLevel;
    readonly logResult?: boolean;
    readonly logName?: string;
}

export function LogInvocation(options: LogInvocationOptions): MethodDecorator;
export function LogInvocation(level: LogLevel, logResult?: boolean): MethodDecorator;
export function LogInvocation(levelOrOptions: LogInvocationOptions | LogLevel, logResult?: boolean): MethodDecorator
{
    let level: LogLevel;
    let logName: string;
    if (typeof levelOrOptions === "object")
    {
        level = levelOrOptions.level;
        logResult = levelOrOptions.logResult;
        logName = levelOrOptions.logName;
    }
    else
    {
        level = levelOrOptions;
    }

    return logInvocation as MethodDecorator;

    function logInvocation(target: Object, key: string | symbol, descriptor: TypedPropertyDescriptor<generalFunction>): TypedPropertyDescriptor<generalFunction> | void
    {
        if (descriptor === undefined)
        {
            throw new Error(`Method Decorator cannot be created with out descriptor!`);
        }

        const targetName = getTargetName(target);
        const classMetadata: ClassMetadata = Reflect.getOwnMetadata(CLASS_METADATA_KEY, target) || {};

        const originalMethod: generalFunction | undefined = descriptor.value;
        const methodName = (originalMethod.name ?? key)?.toString() ?? "ctor";
        const methodMetadata: MethodMetadata = Reflect.getOwnMetadata(METHOD_METADATA_KEY, target, methodName) || {};

        const decoratedMethod = function (this: unknown, ...args: unknown[]): unknown
        {
            const log = getLogger(this, logName ?? targetName, classMetadata);
            if (!log.enabled || level > log.getLogLevel())
            {
                return originalMethod?.apply(this, args);
            }

            const logParamSettings: Array<LoggerParamMetadata> = Reflect.getOwnMetadata(PROPERTY_KEY_LOG_VALUES, target, methodName) || [];
            const params = processArgs(logParamSettings, args);
            const argsStr = stringifyParams(logParamSettings, args);

            // TODO: would a better check to be if a customLogger's name is the same as Target?
            const message = `${methodName}(${argsStr})`;
            logAtLevel(log, level, message, params);

            try
            {
                const executionResult = originalMethod?.apply(this, args);
                if (!logResult)
                {
                    return executionResult;
                }

                if (executionResult instanceof Observable)
                {
                    return executionResult.pipe(
                        tap((result) =>
                        {
                            const tapMessage = `<--- ${message} => Observable.tap => ${stringifyResult(result, methodMetadata)}`;
                            logAtLevel(log, level, tapMessage, params, { result: projectResult(result, methodMetadata) });
                        }),
                        catchError((error) =>
                        {
                            const errorMessage = `<--- ${message} => Observable.catchError => ERROR`;
                            logAtLevel(log, LogLevel.ERROR, errorMessage, params, error);
                            return throwError(() => error);
                        }),
                    );
                }

                if (isPromiseLike(executionResult))
                {
                    return executionResult.then(
                        value =>
                        {
                            const resolveMessage = `<--- ${message} => Promise.then => ${stringifyResult(value, methodMetadata)}`;
                            logAtLevel(log, level, resolveMessage, params, { result: projectResult(value, methodMetadata) });
                            return value;
                        },
                        error =>
                        {
                            const rejectMessage = `<--- ${message} => Promise.catch => ERROR`;
                            logAtLevel(log, LogLevel.ERROR, rejectMessage, params, error);
                            throw error;
                        });
                }

                const resultMessage = `<--- ${message} => ${stringifyResult(executionResult, methodMetadata)}`;
                logAtLevel(log, level, resultMessage, params, { result: projectResult(executionResult, methodMetadata) });
                return executionResult;
            }
            catch (error)
            {
                const errorMessage = `<--- ${message} => ERROR`;
                logAtLevel(log, LogLevel.ERROR, errorMessage, params);
                // Note: Bypassing our logger so that we can let devTools apply any SourceMaps to the error.
                console.error(error);
                throw error;
            }

        };
        Object.defineProperty(
            decoratedMethod,
            'name',
            {
                value: originalMethod
                    ? `${LogInvocation.name}_${originalMethod.name}`
                    : `${LogInvocation.name}_Method`,
                writable: false
            });

        //editing the descriptor/value parameter
        descriptor.value = decoratedMethod as generalFunction;

        // return edited descriptor as opposed to overwriting the descriptor
        return descriptor;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    function getTargetName(target: Record<"constructor", Function> | Function | undefined): string
    {
        if (!target)
        {
            return "UNDEFINED";
        }
        else if (target.constructor && target.constructor.name)
        {
            return target.constructor.name;
        }
        else if (typeof target === "function")
        {
            return target.name;
        }
        return "UNKNOWN";
    }
}
